import { Activity, useState } from "react";
import { Loader2, Plus } from "lucide-react";

import { CheckOperation } from "@workspace/operations/check-operation";
import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@workspace/ui/components/card";

import { useEposContext } from "../epos-context";
import { CreateTabDialog } from "./create-tab-dialog";
import { OpenOrderScreen } from "./open-order-screen";
import { RefundView } from "./refund-view";
import { TabsView } from "./tabs-view";

function NoOrderScreen({
	createNewOrder,
	isCreatingOrder,
}: {
	createNewOrder: () => void;
	isCreatingOrder: boolean;
}) {
	return (
		<>
			<CardContent className="flex flex-col items-center justify-center gap-4">
				<Button
					className="h-16 w-full"
					size="lg"
					// HA-768 / D-HA-24 — the order exists once the server has it; pending until then.
					disabled={isCreatingOrder}
					onClick={() => {
						return createNewOrder();
					}}
				>
					{isCreatingOrder ? (
						<Loader2 className="size-5 animate-spin" />
					) : (
						<Plus className="size-5" />
					)}
					New Order
				</Button>
			</CardContent>
		</>
	);
}

export function EposRightPanel() {
	const {
		staffMembers,
		selectedStaff,
		currentOrder,
		tabs,
		activeTabId,
		refundOrder,
		setRefundOrder,
		setIsRefundDialogOpen,
	} = useEposContext();

	const [isCreateTabOpen, setIsCreateTabOpen] = useState(false);

	const handleCloseRefund = () => {
		setIsRefundDialogOpen(false);
		setRefundOrder(null);
	};

	// Show RefundView when refundOrder is set
	if (refundOrder) {
		return (
			<Card className="max-h-[var(--kiosk-vh,100vh)] overflow-hidden gap-0 p-0">
				<RefundView order={refundOrder} onClose={handleCloseRefund} />
			</Card>
		);
	}

	return (
		<>
			<Activity mode={!currentOrder ? "visible" : "hidden"}>
				<EposEmptyRightPanel onCreateTab={() => setIsCreateTabOpen(true)} />
			</Activity>
			<Activity mode={currentOrder ? "visible" : "hidden"}>
				<Card className="max-h-[var(--kiosk-vh,100vh)] overflow-hidden gap-0 pb-2">
					{!!selectedStaff && (
						<>
							<CardHeader className="pb-3">
								<CardTitle className="flex items-center justify-between text-lg">
									<span>Current Order</span>
									{currentOrder && (
										<Badge variant="outline" className="ml-2">
											<>{currentOrder.id}</>
										</Badge>
									)}
								</CardTitle>
								<CardDescription>
									{currentOrder ? (
										<div className="flex items-center gap-2">
											{currentOrder.tableId && (
												<Badge variant="outline">
													Table {currentOrder.tableName}
												</Badge>
											)}
											{activeTabId && (
												<Badge variant="outline" className=" border-green-700">
													{(() => {
														const tab = tabs.find((t) => t.id === activeTabId);
														if (!tab) return null;
														return tab.name
															? `#${tab.number} | ${tab.name}`
															: `#${tab.number}`;
													})()}
												</Badge>
											)}
											<Badge variant="outline" className="bg-primary/10">
												{
													staffMembers.find((s) => s.id === selectedStaff.id)
														?.personalDetails.preferredName
												}
											</Badge>
										</div>
									) : (
										"No active order"
									)}
								</CardDescription>
							</CardHeader>
							{!!currentOrder && <OpenOrderScreen />}
						</>
					)}
				</Card>
			</Activity>
			<CreateTabDialog
				isOpen={isCreateTabOpen}
				onOpenChange={setIsCreateTabOpen}
			/>
		</>
	);
}

export function EposEmptyRightPanel({
	onCreateTab,
}: {
	onCreateTab: () => void;
}) {
	const { createNewOrder, isCreatingOrder, operations } = useEposContext();

	return (
		<Card className="max-h-[var(--kiosk-vh,100vh)] overflow-hidden gap-0 pb-2">
			<NoOrderScreen
				createNewOrder={createNewOrder}
				isCreatingOrder={isCreatingOrder}
			/>

			<CheckOperation
				operation="staff-terminal-access-tabs"
				operations={operations}
			>
				<TabsView onCreateTab={onCreateTab} />
			</CheckOperation>
		</Card>
	);
}
